import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "store";

import {
  getPresetItem,
  createPreset,
  updatePreset,
  handleClearFields,
  updateCashierConfig,
  getCashierConfig,
  
} from "store/players/presets/slice";

import { closeModal } from "store/modals/slice";

import { NotificationTypes, showNotification } from "store/notifications/slice";

import { Modal } from "components/Modal/Modal";
import Button from "components/buttons/Button";

import PresetsMainTab from "../tabs/presets/PresetsMainTab";
import { ModalPresetsTabs } from "configs/types";
import styles from "./PresetsEditModal.module.scss";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import PresetsLobbyTab from "../tabs/presets/PresetsLobbyTab";
import PresetsSignUpTab from "../tabs/presets/PresetsSignUpTab";
import PresetsCashierTab from "../tabs/presets/PresetsCashierTab";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const PlayersPresetsEditModal: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const payload = useAppSelector((s) => s.presets.payload);
  const isItemFetched = useAppSelector((s) => s.presets.isItemFetched);
  const { isItemFetching, isCashierFetching } = useAppSelector((s) => s.presets);
  const activeCashierGeo = useAppSelector(
    (state) => state.presets.activeCashierGeo,
  );
  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId || payload?.id);
  const [tabs] = useState<ModalPresetsTabs[]>([
    ModalPresetsTabs.main,
    ModalPresetsTabs.lobby,
    ModalPresetsTabs.signUp,
    ModalPresetsTabs.cashier,
  ]);

  const [activeTab, setActiveTab] = useState(ModalPresetsTabs.main);
  const [cashierPayload, setCashierPayload] = useState<any>(null);
  const formErrors = useFormErrors();

  const permissions = usePermissionHelper();

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getPresetItem(entityId));
    }
  }, [entityId, isItemFetched]);

  const isLoaded = () => {
    if (entityId && !isItemFetched) {
      return false;
    }

    return true;
  };

  const loaded = isLoaded();

  const handleCloseClick = () => {
    navigate(location.pathname);

    dispatch(closeModal());

    dispatch(handleClearFields());
  };

  const handleApply = async () => {
    if (activeTab === ModalPresetsTabs.main && payload) {
      if (!isEdit) {
        formErrors.resetErrors();
      }
      try {
        if (isEdit) {
          await dispatch(updatePreset(payload)).unwrap();

          dispatch(
            showNotification({
              title: t("success"),
              message: t("entityUpdateSuccess"),
              type: NotificationTypes.success,
            }),
          );
        } else {
          const newId = await dispatch(createPreset(payload)).unwrap();

          dispatch(
            showNotification({
              title: t("success"),
              message: t("entityCreateSuccess"),
              type: NotificationTypes.success,
            }),
          );
          navigate(
            `${location.pathname}?activeModal=presets&entityId=${newId.id}`,
          );
        }
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });

        return;
      }
    }
    if (activeTab === ModalPresetsTabs.cashier) {
      formErrors.resetErrors();
      try {
        await dispatch(
          updateCashierConfig({
            id: Number(entityId),
            payload: cashierPayload,
          }),
        ).unwrap();

        

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        

        await dispatch(
          getCashierConfig({
            id: Number(entityId),
            geo: activeCashierGeo,
          }),
        );
      } catch (error) {
        handleApiError({ error, t, dispatch, formErrors });
      }

      return;
    }
  };

  const handleTabClick = (tab: ModalPresetsTabs) => {
    if (!isEdit && tab !== ModalPresetsTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
  };

  const renderTab = () => {
    if (activeTab === ModalPresetsTabs.main) {
      return (
        <PresetsMainTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!permissions?.update}
        />
      );
    }
    if (activeTab === ModalPresetsTabs.lobby) {
      return <PresetsLobbyTab readOnly={!permissions?.update} />;
    }
    if (activeTab === ModalPresetsTabs.signUp) {
      return <PresetsSignUpTab readOnly={!permissions?.update} />;
    }
    if (activeTab === ModalPresetsTabs.cashier) {
      return (
        <PresetsCashierTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          resetErrors={formErrors.resetErrors}
          onChange={setCashierPayload}
          readOnly={!permissions?.update}
        />
      );
    }

    return null;
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("preset")}</Modal.Header>

      {!loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            canBeClicked={isEdit}
            onClick={handleTabClick}
          />

          <Modal.Content className={styles.playersPresetsContentWrapper}>
            {renderTab()}
          </Modal.Content>

          {(activeTab === ModalPresetsTabs.main ||
            activeTab === ModalPresetsTabs.cashier) && 
            permissions?.update && (
              <Modal.Footer>
                {activeTab === ModalPresetsTabs.cashier &&
                isCashierFetching ? (
                  <ModalFooterSkeleton />
                ) : (
                  <>
                    <Button
                      isSecondary
                      txt={t("cancel")}
                      handleClick={handleCloseClick}
                    />

                    <Button
                      isPrimary
                      txt={t("save")}
                      handleClick={handleApply}
                      isLoad={isItemFetching}
                    />
                  </>
                )}
              </Modal.Footer>
              )
          }
        </>
      )}
    </Modal>
  );
};

export default PlayersPresetsEditModal;
